(() => {
  if (window.ppFragranceProfileLoaded) return;
  window.ppFragranceProfileLoaded = true;
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const matches = (scope, selector) => [...(scope.matches?.(selector) ? [scope] : []), ...scope.querySelectorAll(selector)];
  const initProfile = (form) => {
    const root = form.closest('.pp-product-page__purchase-core') || form.parentElement;
    if (root.dataset.ppProfileRoot === 'true') return;
    root.dataset.ppProfileRoot = 'true';
    const events = new AbortController();
    const buttons = [...root.querySelectorAll('[data-pp-fragrance-info]')];
    const panel = (button) => document.getElementById(button.getAttribute('aria-controls'));
    const close = (except) => buttons.forEach((button) => {
      if (button === except) return;
      button.setAttribute('aria-expanded', 'false');
      const content = panel(button);
      if (content) content.hidden = true;
    });
    const sync = () => {
      const id = form.querySelector('[name="id"]')?.value;
      root.querySelectorAll('[data-pp-profile-variant]').forEach((group) => { group.hidden = group.dataset.ppProfileVariant !== id; });
      close();
    };
    form.addEventListener('change', (event) => { if (event.target.matches('[name="id"]')) sync(); }, { signal: events.signal });
    buttons.forEach((button) => button.addEventListener('click', () => {
      const open = button.getAttribute('aria-expanded') !== 'true';
      close(button);
      button.setAttribute('aria-expanded', String(open));
      const content = panel(button);
      if (content) content.hidden = !open;
    }, { signal: events.signal }));
    document.addEventListener('click', (event) => { if (!event.target.closest('.pp-fragrance-detail-card')) close(); }, { signal: events.signal });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const open = buttons.find((button) => button.getAttribute('aria-expanded') === 'true');
      if (!open) return;
      close();
      open.focus({ preventScroll: true });
    }, { signal: events.signal });
    root.ppProfileDestroy = () => { events.abort(); delete root.dataset.ppProfileRoot; };
    sync();
  };
  const initDiscovery = (root) => {
    if (root.dataset.ppDiscoveryReady === 'true') return;
    const rail = root.querySelector('[data-pp-recommendation-rail]');
    const dots = root.querySelector('[data-pp-discovery-dots]');
    if (!rail || !dots) return;
    root.dataset.ppDiscoveryReady = 'true';
    const events = new AbortController();
    const items = [...rail.children];
    const count = root.querySelector('[data-pp-discovery-count]');
    const controls = root.querySelector('[data-pp-recommendation-controls]');
    const previous = root.querySelector('[data-pp-recommendation-previous]');
    const next = root.querySelector('[data-pp-recommendation-next]');
    let starts = [];
    let visible = 1;
    let frame = 0;
    const active = () => {
      const rtl = getComputedStyle(rail).direction === 'rtl';
      const edge = rail.getBoundingClientRect()[rtl ? 'right' : 'left'];
      return items.reduce((best, item, index) => {
        const distance = Math.abs(item.getBoundingClientRect()[rtl ? 'right' : 'left'] - edge);
        return distance < best.distance ? { index, distance } : best;
      }, { index: 0, distance: Infinity }).index;
    };
    const move = (index) => {
      const item = items[Math.max(0, Math.min(items.length - 1, index))];
      if (!item) return;
      const style = getComputedStyle(rail);
      const rtl = style.direction === 'rtl';
      const edge = rtl ? 'right' : 'left';
      const padding = parseFloat(style.getPropertyValue('scroll-padding-inline-start')) || 0;
      const delta = item.getBoundingClientRect()[edge] - rail.getBoundingClientRect()[edge] + (rtl ? padding : -padding);
      // Scroll only the rail: scrollIntoView also scrolls hidden page ancestors.
      rail.scrollTo({ left: rail.scrollLeft + delta, behavior: motion.matches ? 'auto' : 'smooth' });
    };
    const update = () => {
      frame = 0;
      if (!items.length) return;
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      visible = Math.max(1, Math.floor((rail.clientWidth + gap) / (items[0].getBoundingClientRect().width + gap)));
      const last = Math.max(0, items.length - visible);
      const nextStarts = [0];
      for (let index = visible; index < last; index += visible) nextStarts.push(index);
      if (last > 0) nextStarts.push(last);
      if (JSON.stringify(starts) !== JSON.stringify(nextStarts)) {
        starts = nextStarts;
        dots.replaceChildren(...starts.map((start, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.setAttribute('aria-label', root.dataset.dotLabel.replace('__INDEX__', index + 1));
          button.addEventListener('click', () => move(start), { signal: events.signal });
          return button;
        }));
      }
      const index = active();
      const page = starts.reduce((best, start, current) => Math.abs(start - index) < Math.abs(starts[best] - index) ? current : best, 0);
      [...dots.children].forEach((button, current) => button.setAttribute('aria-current', String(current === page)));
      if (count) count.textContent = `${index + 1}–${Math.min(items.length, index + visible)} / ${items.length}`;
      const distance = Math.abs(rail.scrollLeft);
      const maximum = rail.scrollWidth - rail.clientWidth;
      if (controls) controls.hidden = maximum <= 2 || items.length < 2;
      if (previous) previous.disabled = distance <= 2;
      if (next) next.disabled = distance >= maximum - 2;
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    previous?.addEventListener('click', () => move(active() - 1), { signal: events.signal });
    next?.addEventListener('click', () => move(active() + 1), { signal: events.signal });
    rail.addEventListener('scroll', schedule, { passive: true, signal: events.signal });
    rail.addEventListener('keydown', (event) => {
      if (event.target !== rail || !['Home', 'End', 'ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      const rtl = getComputedStyle(rail).direction === 'rtl';
      if (event.key === 'Home') move(0);
      else if (event.key === 'End') move(items.length - 1);
      else move(active() + (event.key === 'ArrowRight' ? (rtl ? -1 : 1) : (rtl ? 1 : -1)));
    }, { signal: events.signal });
    const observer = new ResizeObserver(schedule);
    observer.observe(rail);
    root.ppDiscoveryDestroy = () => { events.abort(); observer.disconnect(); if (frame) cancelAnimationFrame(frame); delete root.dataset.ppDiscoveryReady; };
    update();
  };
  const init = (scope = document) => {
    matches(scope, '[data-pp-pdp-form]').forEach(initProfile);
    matches(scope, '[data-pp-scent-discovery]').forEach(initDiscovery);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    matches(event.target, '[data-pp-profile-root]').forEach((root) => root.ppProfileDestroy?.());
    matches(event.target, '[data-pp-scent-discovery]').forEach((root) => root.ppDiscoveryDestroy?.());
  });
})();
